import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import LoginPage from "@pages/LoginPage";
import VerifyPage from "@pages/VerifyPage";
import CanvasPage from "@pages/CanvasPage";
import AdminPage from "@pages/AdminPage";
import PdfTestPage from "@pages/PdfTestPage";
import LabsPage from "@pages/LabsPage";
import ContractsPage from "@pages/labs/ContractsPage";
import ContractWizard from "@components/contracts/ContractWizard";
import ContractReviewPage from "@pages/labs/ContractReviewPage";
import AcceptedContractDetailsPage from "@pages/labs/AcceptedContractDetailsPage";
import ContractEditPage from "@pages/labs/ContractEditPage";
import StarterKitsPage from "@pages/labs/StarterKitsPage";
import SettingsPage from "@pages/labs/SettingsPage";
import PlaybookListPage from "@pages/labs/PlaybookListPage";
import PlaybookEditPage from "@pages/labs/PlaybookEditPage";
import SchemaEditPage from "@pages/labs/SchemaEditPage";
import ChromeExtensionPage from "@pages/labs/ChromeExtensionPage";
import ScanSummaryPage from "@pages/labs/ScanSummaryPage";
import TagMapPage from "@pages/labs/TagMapPage";
import LabsOverviewPage from "@pages/labs/LabsOverviewPage";
import SchemaListPage from "@pages/labs/SchemaListPage";
import DashboardPage from "@pages/labs/DashboardPage";
import AuthGuard from "@components/AuthGuard";
import Layout from "@components/Layout";
import { SchemaType } from "@/types/schema";
import { ProjectProvider } from "@/providers/ProjectProvider";
import { UIProvider } from '@/providers/UIProvider';
import { NotificationProvider } from '@contexts/NotificationContext';
import Notifications from '@components/Notifications';
import { ReactFlowProvider } from 'reactflow';
import { FlowActionsProvider } from '@contexts/FlowActionsContext';
import { ActionSnackbarProvider } from '@contexts/ActionSnackbarContext';
import ActionSnackbar from '@components/common/ActionSnackbar';
import { useAppUpdate } from '@hooks/useAppUpdate';
import DocumentationPanel from '@components/DocumentationPanel';
import { useUIContext } from '@/providers/UIProvider';

// Component to initialize the update hook (must be inside NotificationProvider)
const UpdateInitializer = () => {
  useAppUpdate();
  return null;
};

// Component that consumes UIContext and renders main content + documentation panel
const MainContentWithDocumentation = () => {
  const { isDocumentationPanelOpen, documentationPanelUrl, toggleDocumentationPanel } = useUIContext();

  return (
    <>
      <Outlet />
      <DocumentationPanel 
        isOpen={isDocumentationPanelOpen} 
        onClose={toggleDocumentationPanel} 
        url={documentationPanelUrl}
      />
    </>
  );
};

// Root component that provides context to all routes
const RootProvider = () => {
  return (
    <NotificationProvider>
      <UIProvider>
        <ReactFlowProvider>
          <FlowActionsProvider>
            <ActionSnackbarProvider>
              <ProjectProvider>
                <UpdateInitializer />
                <Notifications />
                <ActionSnackbar />
                {/* Add the UpdateTester component for development testing */}
                {/* <UpdateTester /> */}
                <MainContentWithDocumentation /> {/* Render the new component here */}
              </ProjectProvider>
            </ActionSnackbarProvider>
          </FlowActionsProvider>
        </ReactFlowProvider>
      </UIProvider>
    </NotificationProvider>
  );
};

export const router = createBrowserRouter([
  {
    element: <RootProvider />,
    children: [
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        path: "/verify",
        element: <VerifyPage />,
      },
      {
        path: "/",
        element: (
          <Layout>
            <CanvasPage />
          </Layout>
        ),
      },
      {
        path: "/projects/:projectId",
        element: (
          <Layout>
            <CanvasPage />
          </Layout>
        ),
      },
      {
        path: "/admin",
        element: (
          <AuthGuard requireAdmin>
            <Layout>
              <AdminPage />
            </Layout>
          </AuthGuard>
        ),
      },
      {
        path: "/pdf-test",
        element: (
          <Layout>
            <PdfTestPage />
          </Layout>
        ),
      },
          {
            path: "/labs",
            element: (
              <AuthGuard requireMetarouterUser>
                <LabsPage />
              </AuthGuard>
            ),
            children: [
              {
                path: "", // Default to LabsOverviewPage
                element: <LabsOverviewPage />,
              },
              {
                path: "contracts",
                element: <ContractsPage />,
              },
              {
                path: "contracts/new",
                element: <ContractWizard />,
              },
              {
                path: "contracts/:contractId/edit",
                element: <ContractEditPage />, // Use ContractEditPage
                children: [
                  {
                    path: "", // Default tab
                    element: <Navigate to="details" replace />,
                  },
                  {
                    path: "details", // Renders ContractDetailsTab
                  },
                  {
                    path: "normalization", // Renders DataNormalizationTab
                  },
                ],
              },
              {
                path: "contracts/:contractId/accepted-details", // New route
                element: <AcceptedContractDetailsPage />,
              },
              // Simplified playbook edit routes (similar to schema routes)
              {
                path: "playbooks/new", // Route for new pipeline playbooks
                element: <PlaybookEditPage />,
              },
              {
                path: "integrations/new", // Route for new integration playbooks
                element: <PlaybookEditPage />,
              },
              {
                path: "playbooks/:playbookId/edit", // Route for editing pipeline playbooks
                element: <Navigate to="details" replace />, // Redirect to details tab
              },
              {
                path: "integrations/:playbookId/edit", // Route for editing integration playbooks
                element: <Navigate to="details" replace />, // Redirect to details tab
              },
              {
                path: "playbooks/:playbookId/edit/:tab", // Route with tab parameter for pipeline playbooks
                element: <PlaybookEditPage />,
              },
              {
                path: "integrations/:playbookId/edit/:tab", // Route with tab parameter for integration playbooks
                element: <PlaybookEditPage />,
              },
              {
                path: "integrations",
                element: <PlaybookListPage />, // Route /labs/integrations to PlaybookListPage
              },
              {
                path: "starter-kits",
                element: <StarterKitsPage />,
              },
              {
                path: "playbooks",
                element: <PlaybookListPage />,
              },
              {
                path: "customer-schemas",
                element: <SchemaListPage schemaType={SchemaType.CustomerSchema} />,
              },
              {
                path: "pipeline-schemas",
                element: <SchemaListPage schemaType={SchemaType.PipelineSchema} />,
              },
              {
                path: "vendor-schemas",
                element: <SchemaListPage schemaType={SchemaType.VendorSchema} />,
              },
              {
                path: "unified-schemas",
                element: <SchemaListPage schemaType={SchemaType.UnifiedSchema} />,
              },
              {
                path: ":schemaType/edit/:schemaName",
                element: <SchemaEditPage />,
              },
              {
                path: "chrome-extension",
                element: <ChromeExtensionPage />,
              },
              {
                path: "chrome-extension/:domain",
                element: <ScanSummaryPage />,
              },
              {
                path: "chrome-extension/:domain/tag-map",
                element: <TagMapPage />,
              },
              {
                path: "dashboard",
                element: <DashboardPage />,
              },
              {
                path: "settings/*",
                element: <SettingsPage />,
                children: [
                  {
                    path: "", // Default to integrations
                    element: <Navigate to="integrations" replace />,
                  },
                  {
                    path: "integrations", // Renders IntegrationsTab
                  },
                ],
              },
            ],
          },
      {
        path: "contracts/share/:shareToken",
        element: <ContractReviewPage />,
      },
      {
        path: "*",
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);
